import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, OnChanges, SimpleChanges, Output, ViewChild, forwardRef } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { GoogleMapsService } from '../../services/google-maps.service'

@Component({
  selector: 'app-place-autocomplete',
  template: `<div #container class="place-autocomplete-wrap w-stretch"></div>`,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaceAutocompleteComponent),
      multi: true,
    },
  ],
})
export class PlaceAutocompleteComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>
  @Output() placeChanged = new EventEmitter<any>()
  @Input() options: any = {}
  @Input() disabled: boolean = false

  private placeElement: any = null
  private placeSelectHandler: any = null
  private pendingValue: string = ''
  private onChange = (_value: any) => {/* noop */}
  private onTouched = () => {/* noop */}

  constructor(
    private googleMapsService: GoogleMapsService,
    private ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      const isDisabled = changes['disabled'].currentValue
      if (this.placeElement) {
        this.placeElement.disabled = isDisabled
      }
      if (!isDisabled && !this.placeElement) {
        this.initializeWhenEnabled()
      }
    }
  }

  private async initializeWhenEnabled() {
    await this.googleMapsService.waitForGoogleMapsToLoad()
    await this.waitForPlacesLibrary()
    this.initializePlaceElement()
  }

  async ngOnInit() {
    if (this.disabled) return
    await this.googleMapsService.waitForGoogleMapsToLoad()
    await this.waitForPlacesLibrary()
    this.initializePlaceElement()
  }

  private async waitForPlacesLibrary(): Promise<void> {
    // importLibrary('places') registers the gmp-place-autocomplete custom element;
    // customElements.whenDefined() ensures the element is fully registered before construction
    await (google.maps as any).importLibrary('places')
    await customElements.whenDefined('gmp-place-autocomplete')
  }

  private initializePlaceElement() {
    try {
      // Idempotency guard: both ngOnInit and ngOnChanges (disabled→enabled) can reach this method,
      // and each awaits the Maps load before appending. When the script is already cached, both
      // resume and would each append a gmp-place-autocomplete element (the "two address fields" bug).
      // The first continuation sets this.placeElement synchronously, so a second one bails here.
      if (this.placeElement) {
        return
      }
      if (!(google.maps as any).places?.PlaceAutocompleteElement) {
        throw new Error('PlaceAutocompleteElement not available')
      }

      // Use document.createElement to avoid the 'Illegal constructor' error that occurs
      // when the gmp-place-autocomplete custom element isn't yet registered in the browser
      const el: any = document.createElement('gmp-place-autocomplete')
      Object.assign(el, this.options)

      if (this.disabled) {
        el.disabled = true
      }
      if (this.pendingValue) {
        el.value = this.pendingValue
      }

      this.placeSelectHandler = async (event: any) => {
        const placePrediction = event.placePrediction
        if (!placePrediction) return
        const place = placePrediction.toPlace()
        try {
          await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] })
        } catch (err) {
          console.warn('[PlaceAutocomplete] fetchFields failed:', err)
        }
        const loc = place.location as any
        if (!loc) { console.warn('[PlaceAutocomplete] location unavailable'); return }
        const latVal: number = typeof loc.lat === 'function' ? loc.lat() : loc.lat
        const lngVal: number = typeof loc.lng === 'function' ? loc.lng() : loc.lng
        if (typeof latVal !== 'number' || typeof lngVal !== 'number') {
          console.warn('[PlaceAutocomplete] invalid coords from location:', loc); return
        }
        this.ngZone.run(() => {
          const normalizedPlace = {
            geometry: { location: { lat: () => latVal, lng: () => lngVal } },
            formatted_address: place.formattedAddress,
            name: place.displayName,
          }
          const displayValue = place.formattedAddress || place.displayName || ''
          el.value = displayValue
          this.placeChanged.emit(normalizedPlace)
          this.onChange(displayValue)
          this.onTouched()
        })
      }

      el.addEventListener('gmp-select', this.placeSelectHandler)
      this.container.nativeElement.appendChild(el)
      // Set requestedFields after DOM append so the element's attributeChangedCallback fires
      el.setAttribute('requested-fields', 'displayName,formattedAddress,location')
      this.placeElement = el
    } catch (error) {
      console.error('Error initializing PlaceAutocompleteElement:', error)
    }
  }

  writeValue(value: any): void {
    const v = value || ''
    if (this.placeElement) {
      this.placeElement.value = v
    } else {
      this.pendingValue = v
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    if (this.placeElement) {
      this.placeElement.disabled = isDisabled
    }
  }

  ngOnDestroy() {
    if (this.placeElement && this.placeSelectHandler) {
      this.placeElement.removeEventListener('gmp-select', this.placeSelectHandler)
    }
    this.placeElement = null
  }
}
